import { Copy, User, Users } from 'lucide-react'
import { useState } from 'react'
import { ID_LABELS, displayMembershipNumber, displayPlayerId } from '../utils/patronIdentity'

/**
 * Displays patron identity information consistently across the app.
 * Club-facing: Member # (membership_number). Internal gaming: Player ID (member_id).
 */
export function PatronIdBadge({
  patronId,
  memberId,
  membershipNumber,
  playerId,
  visitorId,
  profileType,
  variant = 'full',
  showCopy = true,
  showPlayerId = true,
  className = '',
}) {
  const [copied, setCopied] = useState(null)

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 1500)
  }

  const clubNumber =
    membershipNumber ||
    displayMembershipNumber({ membership_number: membershipNumber, member_id: memberId })
  const igtPlayerId =
    playerId || displayPlayerId({ membership_number: clubNumber, member_id: memberId, player_id: playerId })

  const isMember = profileType === 'MEMBER' || (clubNumber && !visitorId)
  const isVisitor = profileType === 'VISITOR' || (!clubNumber && !igtPlayerId && visitorId)
  const hasBothIds = clubNumber && visitorId

  if (variant === 'inline') {
    const parts = []
    if (patronId) parts.push(patronId)
    if (clubNumber) parts.push(`${ID_LABELS.membership}: ${clubNumber}`)
    if (showPlayerId && igtPlayerId && igtPlayerId !== clubNumber) {
      parts.push(`${ID_LABELS.player}: ${igtPlayerId}`)
    }
    if (visitorId) parts.push(`V: ${visitorId}`)
    return (
      <span className={`font-mono text-xs ${className}`}>
        {parts.join(' · ')}
      </span>
    )
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 text-xs flex-wrap ${className}`}>
        {patronId && (
          <span className="font-mono bg-slate-100 px-2 py-0.5 rounded" title={ID_LABELS.patron}>
            {patronId}
          </span>
        )}
        {clubNumber && (
          <span className="font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded" title={ID_LABELS.membership}>
            {clubNumber}
          </span>
        )}
        {showPlayerId && igtPlayerId && igtPlayerId !== clubNumber && (
          <span className="font-mono bg-slate-50 text-slate-600 px-2 py-0.5 rounded" title={ID_LABELS.player}>
            {igtPlayerId}
          </span>
        )}
        {visitorId && (
          <span className="font-mono bg-amber-50 text-amber-700 px-2 py-0.5 rounded" title={ID_LABELS.visitor}>
            {visitorId}
          </span>
        )}
      </div>
    )
  }

  const row = (label, value, key, icon = null) => (
    <div className="flex items-center gap-2" key={key}>
      <span className="text-xs text-gray-500 w-24 flex items-center gap-1 shrink-0">
        {icon}
        {label}
      </span>
      <span className="font-mono text-xs bg-slate-50 px-2 py-0.5 rounded">{value}</span>
      {showCopy && (
        <button
          type="button"
          onClick={() => copyToClipboard(value, key)}
          className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
          title={`Copy ${label}`}
        >
          {copied === key ? (
            <span className="text-green-600 text-[10px]">Copied</span>
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      )}
    </div>
  )

  return (
    <div className={`space-y-1 ${className}`}>
      {patronId && row(ID_LABELS.patron, patronId, 'patron')}
      {clubNumber && row(ID_LABELS.membership, clubNumber, 'member', <Users className="h-3 w-3" />)}
      {showPlayerId && igtPlayerId && igtPlayerId !== clubNumber &&
        row(ID_LABELS.player, igtPlayerId, 'player')}
      {visitorId && row(ID_LABELS.visitor, visitorId, 'visitor', <User className="h-3 w-3" />)}
      {hasBothIds && isVisitor && (
        <div className="text-[10px] text-gray-400 mt-1">Visitor linked to club membership</div>
      )}
    </div>
  )
}

export function PatronIdInline({ patronId, memberId, membershipNumber, playerId, visitorId }) {
  return (
    <PatronIdBadge
      patronId={patronId}
      memberId={memberId}
      membershipNumber={membershipNumber}
      playerId={playerId}
      visitorId={visitorId}
      variant="inline"
      showCopy={false}
    />
  )
}

export default PatronIdBadge
